import {DialogOpenChangeDetails, DialogRootProps as ChakraDialogProps} from "@chakra-ui3/react";
import {ComponentType, FC, Suspense, useCallback} from "react";
import Loading from "../components/Loading";
import {useModalDiagnostics} from "../utils/diagnostics";
import {DialogCloseTrigger, DialogContent, DialogRoot} from "../components/ui/dialog";

/** Additional props passed to your component when wrapping with `withDialog`. */
export type InnerDialogProps = {
  /** Call this to close the dialog. */
  onClose: () => void;
};

/** Props added to a component when wrapped with `withDialog`. */
type WithDialogExtraProps = {
  /** Determines whether the dialog is open. */
  isOpen?: boolean; // Compatibility with ModalProvider
  open?: boolean;

  /** Called when the dialog is closed. */
  onClose: () => void;

  /** Called when the dialog has fully exited. */
  onExitComplete?: () => void;
};

/** These props should not be defined on the inner dialog component passed to
 * `withDialog`, because they conflict with the added outer props. */
type RestrictedDialogProps = {[K in keyof Omit<WithDialogExtraProps, keyof InnerDialogProps>]?: never};

/** The final props on the component returned by `withDialog<P>(...)` */
type WithDialogProps<P> = WithDialogExtraProps & Omit<P, keyof InnerDialogProps | keyof RestrictedDialogProps>;

/** Other props passed as a 2nd argument to `withDialog` to customize the dialog. */
type OtherDialogProps = Omit<ChakraDialogProps, keyof WithDialogExtraProps | "children">;

export function withDialog<P extends InnerDialogProps & RestrictedDialogProps>(
  WrappedComponent: ComponentType<P>,
  otherProps?: OtherDialogProps,
): FC<WithDialogProps<P>> {
  return ({open, isOpen, onClose, onExitComplete, ...rest}: WithDialogProps<P>) => {
    const compatOpen = open ?? isOpen ?? false;
    useModalDiagnostics(compatOpen);

    const onOpenChange = useCallback(
      (details: DialogOpenChangeDetails) => {
        if (!details.open) {
          onClose.call(null);
        }
      },
      [onClose],
    );

    const innerDialogProps: InnerDialogProps = {onClose};

    return (
      <DialogRoot open={compatOpen} onOpenChange={onOpenChange} onExitComplete={onExitComplete} {...otherProps}>
        <DialogContent>
          <Suspense fallback={<Loading m={8} />}>
            <WrappedComponent {...(rest as unknown as P)} {...innerDialogProps} />
          </Suspense>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    );
  };
}
